import React from "react"
import {
    BaselineDataBase,
    InstructionsArgs
} from "../../BaselineDataBase/BaselineDataBase"
import { Title, Text, Box, Link, Panel, Tabbed } from "dash-styled"
import { NavButton } from "../../../../../../../../../Common/NavButton/NavButton"
import { routePaths } from "../../../../../../../../../../Routes"
import { CodeBlock } from "../../../../../../../Common/CodeBlock/CodeBlock"
import { CreateFileUrlLink } from "../../../../../../../Common/CreateFileUrlLink/CreateFileUrlLink"
import { MainThunkDispatch } from "../../../../../../../../../../Reducers"
import { connect } from "react-redux"
import { push } from "connected-react-router"
import cURLIcon from "../../../../../../../../../Common/Assets/curl_symbol.png"
import pythonIcon from "../../../../../../../../../Common/Assets/python_icon.png"
import cliIcon from "../../../../../../../../../Common/Assets/terminal_symbol.png"
import { sdkExamples } from "../../../../../../../../../../Helpers/Functions/sdk"

export enum BaselineDataSDKType {
    curl,
    python,
    cli
}

interface BaselineDataSDKProps {
    orgUsername: string
    appID: string
    type: BaselineDataSDKType
}

interface DispatchProps {
    changeTab: (index: number) => void
}

type Props = BaselineDataSDKProps & DispatchProps

export const BaselineDataSDKIsolated = ({
    orgUsername,
    appID,
    type,
    changeTab
}: Props) => {
    const { groundTruth } = sdkExamples
    const renderSidebar = () => {
        return (
            <div>
                <CreateFileUrlLink orgUsername={orgUsername} />
                <NavButton
                    variant="text"
                    color="plain"
                    className="my-4"
                    to={routePaths.appCustomizeBaselineDataAddDataSingle(
                        orgUsername,
                        appID
                    )}
                >
                    Quick create ground truth data
                </NavButton>
                <NavButton
                    variant="text"
                    color="plain"
                    marginBottom="3"
                    to={routePaths.appCustomizeBaselineDataAddDataUpload(
                        orgUsername,
                        appID
                    )}
                >
                    Create ground truth data from JSON
                </NavButton>
            </div>
        )
    }
    const render = ({
        instance,
        apiKey,
        apiUrl,
        example
    }: InstructionsArgs) => {
        const code = {
            input: (example && example.input) || "<Your Input Data>",
            label:
                (example && example.output && example.output) ||
                "<Your Output Data>"
        }

        const exampleData = [
            {
                label: "cURL",
                tabImage: cURLIcon,
                content: (
                    <div className="block p-4 overflow-auto font-mono">
                        <pre>
                            {apiKey === undefined
                                ? ""
                                : groundTruth.curl.create(
                                      apiUrl,
                                      apiKey,
                                      instance.uuid,
                                      JSON.stringify(code)
                                  )}
                        </pre>
                    </div>
                )
            },
            {
                label: "Python",
                tabImage: pythonIcon,
                content: (
                    <div className="block p-4 overflow-auto font-mono">
                        <pre>
                            {apiKey === undefined
                                ? ""
                                : groundTruth.python.create(
                                      apiKey,
                                      instance.uuid,
                                      JSON.stringify(code.input),
                                      JSON.stringify(code.label)
                                  )}
                        </pre>
                    </div>
                )
            },
            {
                label: "CLI",
                tabImage: cliIcon,
                content: (
                    <div className="block p-4 overflow-auto font-mono">
                        <pre>
                            {apiKey === undefined
                                ? ""
                                : groundTruth.cli.create(
                                      instance.uuid,
                                      JSON.stringify(code.input),
                                      JSON.stringify(code.label)
                                  )}
                        </pre>
                    </div>
                )
            }
        ]

        const sampleData = example && example.output && (
            <Box>
                <Box marginBottom="2">
                    <Title variant="heading2">Example data</Title>
                </Box>
                <Panel>
                    <CodeBlock>
                        {{ input: example.input, label: example.output }}
                    </CodeBlock>
                </Panel>
            </Box>
        )

        return (
            <div>
                <Text>
                    Our SDK, including installation instructions,{" "}
                    <Link to="https://github.com/mysuperai/superai-api-client">
                        is available on Github.
                    </Link>
                </Text>
                {(!example || !example.output || !example.isValidJob) && (
                    <Text>
                        Please note the examples below are illustrative only.
                        Ground truth data created using these values may be
                        inaccurate and will lead to low quality labels and
                        erroneous measurements. To ensure you have the correct
                        structure and values for your ground truth data, you
                        will need at least one processed data point.
                    </Text>
                )}
                <Text>
                    Choose a platform, copy and paste the code samples and run
                    on your computer to create ground truth data. Inputs and
                    outputs need to match their respective schemas.{" "}
                    <Link to={routePaths.docsApiReference()}>
                        View the API docs for more information.
                    </Link>
                </Text>
                <Box marginBottom="4">
                    <Panel noPadding={true} className="overflow-hidden">
                        <Tabbed
                            tabbedData={exampleData}
                            selectedTabIndex={type} // index of tab
                            onTabSelect={changeTab}
                            variant={"image"}
                        >
                            {exampleData[type].content}
                        </Tabbed>
                    </Panel>
                </Box>
                <Box marginBottom="4">{sampleData}</Box>
            </div>
        )
    }

    return (
        <BaselineDataBase
            orgUsername={orgUsername}
            id={appID}
            title="Add ground-truth data"
            breadcrumbs={["Ground-truth Data"]}
            child={render}
            renderSidebar={renderSidebar}
        />
    )
}

const mapDispatchToProps = (
    dispatch: MainThunkDispatch,
    { appID, orgUsername }: BaselineDataSDKProps
): DispatchProps => {
    return {
        changeTab: (index: number) => {
            switch (index) {
                case BaselineDataSDKType.cli:
                    return dispatch(
                        push(
                            routePaths.appCustomizeBaselineDataAddDataCLI(
                                orgUsername,
                                appID
                            )
                        )
                    )
                case BaselineDataSDKType.python:
                    return dispatch(
                        push(
                            routePaths.appCustomizeBaselineDataAddDataPython(
                                orgUsername,
                                appID
                            )
                        )
                    )
                case BaselineDataSDKType.curl:
                    return dispatch(
                        push(
                            routePaths.appCustomizeBaselineDataAddDataCurl(
                                orgUsername,
                                appID
                            )
                        )
                    )
                default:
                    return
            }
        }
    }
}

export const BaselineDataSDK = connect(
    null,
    mapDispatchToProps
)(BaselineDataSDKIsolated)
